import React from 'react';
import { Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-[#F4F6F8] to-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center mb-6">
            <Shield className="text-[#0F4C81]" size={48} />
          </div>
          <h1 className="text-5xl font-bold text-[#12324A] mb-6 text-center">
            Privacy Policy
          </h1>
          <p className="text-lg text-[#5F6C7B] text-center">
            Last Updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <div className="bg-[#F4F6F8] rounded-xl p-6 mb-8">
              <p className="text-[#5F6C7B] m-0">
                At TouchScreenMD, we are committed to protecting your privacy and safeguarding your personal health information. This Privacy Policy explains how we collect, use, and protect your information.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Information We Collect</h2>
            <p className="text-[#5F6C7B] mb-4">
              We collect information necessary to provide you with quality healthcare services:
            </p>
            <ul className="space-y-2 mb-6">
              <li className="text-[#5F6C7B]">Personal identification information (name, date of birth, address, phone number, email)</li>
              <li className="text-[#5F6C7B]">Health information (medical history, symptoms, medications, allergies)</li>
              <li className="text-[#5F6C7B]">Insurance information (insurance provider, member ID, coverage details)</li>
              <li className="text-[#5F6C7B]">Payment information (credit card details, billing address)</li>
              <li className="text-[#5F6C7B]">Technical information (IP address, browser type, device information)</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">How We Use Your Information</h2>
            <p className="text-[#5F6C7B] mb-4">
              We use your information to:
            </p>
            <ul className="space-y-2 mb-6">
              <li className="text-[#5F6C7B]">Provide telehealth services and medical care</li>
              <li className="text-[#5F6C7B]">Process payments and insurance claims</li>
              <li className="text-[#5F6C7B]">Communicate with you about your care</li>
              <li className="text-[#5F6C7B]">Improve our services and user experience</li>
              <li className="text-[#5F6C7B]">Comply with legal and regulatory requirements</li>
              <li className="text-[#5F6C7B]">Maintain security and prevent fraud</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Information Sharing</h2>
            <p className="text-[#5F6C7B] mb-4">
              We do not sell your personal health information. We may share your information with:
            </p>
            <ul className="space-y-2 mb-6">
              <li className="text-[#5F6C7B]">Healthcare providers involved in your care</li>
              <li className="text-[#5F6C7B]">Insurance companies for billing and coverage verification</li>
              <li className="text-[#5F6C7B]">Pharmacies for prescription fulfillment</li>
              <li className="text-[#5F6C7B]">Service providers who assist with our operations (with strict confidentiality agreements)</li>
              <li className="text-[#5F6C7B]">Legal authorities when required by law</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Data Security</h2>
            <p className="text-[#5F6C7B] mb-6">
              We implement industry-standard safeguards designed to protect your information, including:
            </p>
            <ul className="space-y-2 mb-6">
              <li className="text-[#5F6C7B]">Encrypted data transmission and storage</li>
              <li className="text-[#5F6C7B]">Secure video conferencing technology</li>
              <li className="text-[#5F6C7B]">Access controls and authentication measures</li>
              <li className="text-[#5F6C7B]">Regular security assessments and updates</li>
              <li className="text-[#5F6C7B]">Employee training on privacy and security</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Your Rights</h2>
            <p className="text-[#5F6C7B] mb-4">
              You have the right to:
            </p>
            <ul className="space-y-2 mb-6">
              <li className="text-[#5F6C7B]">Access your personal health information</li>
              <li className="text-[#5F6C7B]">Request corrections to your information</li>
              <li className="text-[#5F6C7B]">Request restrictions on how we use your information</li>
              <li className="text-[#5F6C7B]">Receive an accounting of disclosures</li>
              <li className="text-[#5F6C7B]">Request a copy of this privacy policy</li>
              <li className="text-[#5F6C7B]">File a complaint if you believe your privacy rights have been violated</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Cookies and Tracking</h2>
            <p className="text-[#5F6C7B] mb-6">
              We use cookies and similar technologies to improve user experience, analyze site usage, and personalize content. You can control cookie settings through your browser.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Changes to This Policy</h2>
            <p className="text-[#5F6C7B] mb-6">
              We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on our website with an updated effective date.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Contact Us</h2>
            <p className="text-[#5F6C7B] mb-4">
              If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
            </p>
            <div className="bg-[#F4F6F8] rounded-xl p-6">
              <p className="text-[#5F6C7B] m-0"><strong>Email:</strong> privacy@touchscreenmd.com</p>
              <p className="text-[#5F6C7B] m-0 mt-2"><strong>Phone:</strong> 1-800-TOUCH-MD</p>
              <p className="text-[#5F6C7B] m-0 mt-2"><strong>Mail:</strong> TouchScreenMD Privacy Office, United States</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
